import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Stack, Typography, Box } from "@mui/material";
import { useLSContext } from '../Context/useLSContext';
import { useDataContext } from '../Context/useDataContext';
import { Add, Circle } from '@mui/icons-material';

interface hbhProps {
    allowEdit: boolean;
    factor: number;
    roundReview?: boolean;
    clearHeaders?: boolean;
    scoreDataOverride?: any;
    holeSettings: any;
    highlightCurrentHole?: boolean;
    selectedCell?: { hole: number; scoreCode: string };
    onCellSelect?: (hole: number, scoreCode: string) => void;
    editingEnabled?: boolean;
    hideOuterBorder?: boolean;
}

const holeValueStyle = (displayScore: number, teeSetHole: any) => {

    return {
        width: 26,
        height: 26,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: displayScore < teeSetHole.par ? '50%' : '4px',
        backgroundColor: displayScore === 0 ? 'transparent' : 
            displayScore < teeSetHole.par ? 'rgb(166, 221, 178)' :
            displayScore > teeSetHole.par ? 'rgb(240, 131, 131)' :
            'transparent',
        border: displayScore === 0 ? '1px solid transparent' : 
        displayScore < teeSetHole.par - 1 ? '4px double rgb(20, 68, 24)' :
        displayScore > teeSetHole.par + 1 ? '4px double rgb(112, 18, 18)' :
        '4px solid transparent'
    }
}   

const holeStackStyle = {
    width: 40, 
    height: 55,
    minWidth: 40,
    borderBottom: '1px solid #ddd',
    justifyContent: 'center',
    alignItems: 'center',
}

const aggStackStyle = {
    width: 50, 
    height: 55, 
    minWidth: 50,
    borderBottom: '1px solid #ddd',
    justifyContent: 'center',
    alignItems: 'center',
    fontWeight: '550'
}

export const OpponentScoringHBH = ({
    allowEdit,
    factor,
    roundReview,
    clearHeaders,
    holeSettings,
    highlightCurrentHole = true,
    selectedCell,
    onCellSelect,
    editingEnabled = true,
    hideOuterBorder = false,
}: hbhProps) => {
    const { windowSize, appTheme } = useLSContext();
    const { registrationData, scoreData, courseData, eventData, scoreCode, currentRound } = useDataContext();
    const [localScoreData, setLocalScoreData] = useState<any>(scoreData);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setLocalScoreData(scoreData);
    }, [scoreData]);

    // Safely access totalHoles with fallback
    const totalHoles = eventData?.rounds?.[currentRound-1]?.totalHoles || 18;

    // Memoize reducedRegistrations to prevent recalculation on every render
    // Sort so the current player (matching scoreCode) always appears first, matching ScoreBlock order
    const reducedRegistrations = useMemo(() => {
        const deduplicated = registrationData?.reduce((acc: any, registration: any) => {
            if (!acc.some((item: any) => item.scoreCode === registration.scoreCode)) {
                acc.push(registration);
            }
            return acc;
        }, []) || [];

        return deduplicated.sort((a: any, b: any) => {
            if (a.scoreCode === scoreCode) return -1;
            if (b.scoreCode === scoreCode) return 1;
            return 0;
        });
    }, [registrationData, scoreCode]);

    const teeSet = useMemo(() => {
        // console.log('teeSet', courseData?.teeSets?.[0]);
        return courseData?.teeSets?.[0];
    }, [courseData]);

    // Memoize the getInitials function
    const getInitials = useMemo(() => {
        return (scoreCode: string) => {
            const regs = registrationData?.filter((registration: any) => registration.scoreCode === scoreCode) || [];
            
            // If there's more than one player, use initials with / separator
            if (regs.length > 1) {
                return regs.map((registration: any) => {
                    // Get the first initial of each name part
                    return registration.playerName.split(' ')
                        .map((n: string) => n[0])
                        .join('');
                }).join(' / ');
            } 
            // If there's only one player, use the first 4 characters of their name
            else if (regs.length === 1) {
                const name = regs[0].playerName.trim();
                return name.length > 4 ? name.substring(0, 4) : name;
            }
            
            return '';
        };
    }, [registrationData]);

    // Memoize hole scores to prevent recalculation
    /* const holeScores = useMemo(() => {
        if (!scoreData?.[0]?.roundScores?.[currentRound-1]?.holeScores) {
            return [];
        }

        return scoreData[0].roundScores[currentRound-1].holeScores;
    }, [scoreData, currentRound]); */

    // Memoize front nine and back nine totals for each player
    const playerTotals = useMemo(() => {
        const totals: Record<string, { front: number, back: number, total: number, totalPar: number }> = {};

        reducedRegistrations.forEach((registration: any) => {
            const isMarker = eventData.scoreType?.toLowerCase() === 'opponent' ? registration.scoreCode === scoreCode : false;

            const holeScores = localScoreData.find((score: any) => 
                isMarker ? score.roundScores[currentRound-1].markerScoreCode === registration.scoreCode 
                : score.roundScores[currentRound-1].playerScoreCode === registration.scoreCode)
                ?.roundScores[currentRound-1].holeScores;
            if (!holeScores.length) {
                totals[registration.scoreCode] = { front: 0, back: 0, total: 0, totalPar: 0 };
                return;
            }

            // Calculate front nine total (holes 1-9)
            const frontNineTotal = holeScores
                .filter((hole: any) => hole.holeNumber >= 1 && hole.holeNumber <= 9)
                .reduce((total: number, hole: any) => {
                    const score = isMarker ? hole.markerScore : hole.playerScore;
                    return total + (score || 0);
                }, 0);
            
            // Calculate back nine total (holes 10-18)
            const backNineTotal = holeScores
                .filter((hole: any) => hole.holeNumber >= 10 && hole.holeNumber <= 18)
                .reduce((total: number, hole: any) => {
                    const score = isMarker ? hole.markerScore : hole.playerScore;
                    return total + (score || 0);
                }, 0);

            // Calculate total par for the course
            const totalPar = holeScores.reduce((acc: number, score: any) => {
                if (score.playerScore !== 0 && score.playerScore !== null) {
                    return acc + score.par;
                }
                return acc;
            }, 0);
            
            totals[registration.scoreCode] = {
                front: frontNineTotal,
                back: backNineTotal,
                total: frontNineTotal + backNineTotal,
                totalPar: totalPar
            };
        });

        return totals;
    }, [reducedRegistrations, scoreCode, scoreData, localScoreData, eventData, currentRound]);

    const scrollToHole = selectedCell?.hole ?? holeSettings.hole;

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container || !scrollToHole) return;

        const column = container.querySelector(
            `[data-hole="${scrollToHole}"]`,
        ) as HTMLElement | null;
        if (!column) return;

        const containerRect = container.getBoundingClientRect();
        const columnRect = column.getBoundingClientRect();
        const columnOffsetInContent =
            columnRect.left - containerRect.left + container.scrollLeft;
        const target =
            columnOffsetInContent - container.clientWidth / 2 + column.offsetWidth / 2;
        const maxScroll = Math.max(0, container.scrollWidth - container.clientWidth);
        container.scrollLeft = Math.max(0, Math.min(target, maxScroll));
    }, [scrollToHole, windowSize.width, totalHoles, reducedRegistrations.length]);

    const isCellSelected = (holeNumber: number, regScoreCode: string) =>
        selectedCell?.hole === holeNumber && selectedCell?.scoreCode === regScoreCode;

    const isCellInteractive = Boolean(onCellSelect) && editingEnabled !== false;

    const handleCellPointerDown = (
        e: React.PointerEvent,
        holeNumber: number,
        regScoreCode: string,
    ) => {
        if (!isCellInteractive) return;
        e.preventDefault();
        onCellSelect?.(holeNumber, regScoreCode);
    };

    const handleHoleHeaderPointerDown = (
        e: React.PointerEvent,
        holeNumber: number,
    ) => {
        if (!isCellInteractive) return;
        e.preventDefault();
        e.stopPropagation();
        const activeScoreCode = selectedCell?.scoreCode ?? reducedRegistrations[0]?.scoreCode;
        if (activeScoreCode) {
            onCellSelect?.(holeNumber, activeScoreCode);
        }
    };

    return (
        <Stack direction="column" spacing={1}
            sx={{
                width: '100%', overflowx: 'auto', maxWidth: windowSize.width - 20,
                backgroundColor: 'sepia.background',
                ...(hideOuterBorder ? {} : {
                    border: '1px solid',
                    borderColor: 'sepia.extraLight',
                    borderRadius: 2,
                }),
                overflow: 'hidden',
            }}>
                <Stack direction="row" spacing={0} width={'100%'}>
                    {/* Labels column */}
                    <Stack direction="column" spacing={0} 
                        sx={{ width: 60, alignItems: 'center', justifyContent: 'center', 
                            padding: '0px 2px', borderRight: '1px solid darkgray'
                        }}>
                        <Stack 
                            key={`#`} 
                            sx={{ 
                                width: 70, 
                                height: 30, 
                                justifyContent: 'center',
                                alignItems: 'center',
                                backgroundColor: 'sepia.background'
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ height: 24*factor, textAlign: 'right'}}>Hole</Typography>
                        </Stack>
                        {reducedRegistrations.map((registration: any) => (
                            <Stack 
                                key={`name-${registration.scoreCode}`} 
                                sx={{ 
                                    width: 70, 
                                    height: 60, 
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    backgroundColor: 'sepia.background',
                                }}
                            >
                                <Typography variant="subtitle1"
                                sx={{ width: '90%', height: '100%', textAlign: 'center', textWrap: 'wrap',
                                    alignContent: 'center', justifyContent: 'center'
                                }}>
                                    {getInitials(registration.scoreCode)}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                    
                    {/* Data columns */}
                    <Stack direction="column" spacing={0} ref={scrollContainerRef}
                        sx={{ width: `calc(100% - 60px)`, alignContent: 'center',
                        overflowX: 'auto'
                     }}>
                        {/* Hole numbers */}
                        <Stack direction="row" spacing={0} sx={{ }}>
                            {Array.from({length: totalHoles}, (_, i) => {
                                // Calculate totals for front 9 and back 9 when needed
                                const isFrontNineEnd = i === 8; // Hole 9
                                const isBackNineEnd = i === 17; // Hole 18

                                return (
                                    <React.Fragment key={`title-fragment-${i}`}>
                                        <Stack 
                                            key={`title-${i}`}
                                            data-hole={i + 1}
                                            tabIndex={isCellInteractive ? -1 : undefined}
                                            onPointerDown={(e) => handleHoleHeaderPointerDown(e, i + 1)}
                                            sx={{ 
                                                width: 40, 
                                                height: 30,
                                                minWidth: 40,
                                                borderBottom: '1px solid darkgray',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                backgroundColor: highlightCurrentHole && i + 1 === holeSettings.hole
                                                    ? 'secondary.light'
                                                    : 'sepia.background',
                                                cursor: isCellInteractive ? 'pointer' : 'default',
                                            }}
                                        >
                                            <Typography variant="subtitle2" sx={{ height: 24*factor, textAlign: 'right' }}>
                                                {i+1}
                                            </Typography>
                                        </Stack>
                                        
                                        {isFrontNineEnd && (
                                            <Stack 
                                                key="front-nine-header"
                                                sx={{ 
                                                    width: 50, 
                                                    height: 30, 
                                                    minWidth: 50,
                                                    borderBottom: '1px solid darkgray',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    backgroundColor: 'sepia.extraLight',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                <Typography variant="subtitle2" sx={{ height: 24*factor, textAlign: 'right' }}>
                                                    Front
                                                </Typography>
                                            </Stack>
                                        )}
                                        
                                        {isBackNineEnd && (
                                            <Stack 
                                                key="back-nine-header"
                                                sx={{ 
                                                    width: 50, 
                                                    height: 30, 
                                                    minWidth: 50,
                                                    borderBottom: '1px solid darkgray',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    backgroundColor: 'sepia.extraLight',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                <Typography variant="subtitle2" sx={{ height: 24*factor, textAlign: 'right' }}>
                                                    Back
                                                </Typography>
                                            </Stack>
                                        )}
                                        
                                        {i === totalHoles-1 && (
                                            <React.Fragment key="total-headers">
                                                <Stack 
                                                    key="total-header"
                                                    sx={{ 
                                                        width: 50, 
                                                        height: 30, 
                                                        minWidth: 50,
                                                        borderBottom: '1px solid darkgray',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        backgroundColor: 'primary.extraLight',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    <Typography variant="subtitle2" sx={{ height: 24*factor, textAlign: 'right' }}>
                                                        Total
                                                    </Typography>
                                                </Stack>
                                                <Stack 
                                                    key="par-header"
                                                    sx={{ 
                                                        width: 50, 
                                                        height: 30, 
                                                        minWidth: 50,
                                                        borderBottom: '1px solid darkgray',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        backgroundColor: 'sepia.background',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    <Typography variant="subtitle2" sx={{ height: 24*factor, textAlign: 'right' }}>
                                                        +/-
                                                    </Typography>
                                                </Stack>
                                            </React.Fragment>
                                        )}
                                    </React.Fragment>
                                );
                            })} 
                        </Stack>
                        
                        {/* Player scores */}
                        <Stack direction="column" spacing={0} sx={{ width: windowSize.width - 60 }}>
                            {reducedRegistrations.map((registration: any) => {
                                const isMarker = eventData.scoreType?.toLowerCase() === 'opponent' ? registration.scoreCode === scoreCode : false;
                                const playerTotal = playerTotals[registration.scoreCode] || { front: 0, back: 0, total: 0 };
                                
                                return (
                                    <Stack key={registration.scoreCode} direction="row" spacing={0}>
                                        {Array.from({length: totalHoles}, (_, i) => {
                                            // Get the hole number (1-indexed)
                                            const holeNumber = i + 1;
                                            
                                            // Find the hole score from holeScores
                                            const holeScores = localScoreData.find((score: any) => 
                                                isMarker ? score.roundScores[currentRound-1].markerScoreCode === registration.scoreCode 
                                                : score.roundScores[currentRound-1].playerScoreCode === registration.scoreCode)
                                                ?.roundScores[currentRound-1].holeScores[holeNumber-1];
                                            ;
                                            
                                            // Determine which score to display
                                            const displayScore = isMarker 
                                                ? holeScores?.markerScore 
                                                : holeScores?.playerScore;

                                            // Calculate totals for front 9 and back 9 when needed
                                            const isFrontNineEnd = i === 8; // Hole 9
                                            const isBackNineEnd = i === 17; // Hole 18
                                            
                                            const cellSelected = isCellSelected(holeNumber, registration.scoreCode);
                                            
                                            return (
                                                <React.Fragment key={`hole-fragment-${holeNumber}`}>
                                                    <Stack 
                                                        key={`hole-${holeNumber}`}
                                                        tabIndex={isCellInteractive ? -1 : undefined}
                                                        onPointerDown={(e) => handleCellPointerDown(e, holeNumber, registration.scoreCode)}
                                                        sx={{ 
                                                            position: 'relative',
                                                            width: 40, 
                                                            height: 60, 
                                                            minWidth: 40,
                                                            borderBottom: '1px solid darkgray',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            backgroundColor: highlightCurrentHole && holeNumber === holeSettings.hole
                                                                ? 'secondary.light'
                                                                : 'sepia.background',
                                                            cursor: isCellInteractive ? 'pointer' : 'default',
                                                            boxSizing: 'border-box',
                                                            ...(cellSelected && {
                                                                border: `2px solid ${appTheme.palette.primary.light}`,
                                                            }),
                                                        }}
                                                    >
                                                        <Stack key={`hole-${holeNumber}-circle-stack`}
                                                            direction={'row'} 
                                                            sx={{ 
                                                                alignItems: 'center', 
                                                                justifyContent: 'flex-start',
                                                                marginLeft: '5px',
                                                                gap: '3px',
                                                                padding: 0,
                                                                zIndex: 10,
                                                                position: 'absolute',
                                                                top: -45,
                                                                left: 8,
                                                                right: 0,
                                                                bottom: 0,
                                                                margin: 0,
                                                                pointerEvents: 'none',

                                                        }}>
                                                            {isMarker && eventData.scoreFormat !== 'gross' &&
                                                                (holeScores?.markerHcpAdj ?? 0) !== 0 &&
                                                                ((holeScores.markerHcpAdj ?? 0) > 0
                                                                    ? Array.from({ length: holeScores.markerHcpAdj }, (_, index) => (
                                                                        <Circle key={`hole-${holeNumber}-marker-circle-${index}`}
                                                                            sx={{ fontSize: 10 * factor, color: 'primary.main' }} />
                                                                      ))
                                                                    : Array.from({ length: Math.abs(holeScores.markerHcpAdj ?? 0) }, (_, index) => (
                                                                        <Add key={`hole-${holeNumber}-marker-add-${index}`}
                                                                            sx={{ fontSize: 12 * factor, color: 'primary.main', fontWeight: 700 }} />
                                                                      )))
                                                            }
                                                            {!isMarker && eventData.scoreFormat !== 'gross' &&
                                                                (holeScores?.playerHcpAdj ?? 0) !== 0 &&
                                                                ((holeScores.playerHcpAdj ?? 0) > 0
                                                                    ? Array.from({ length: holeScores.playerHcpAdj }, (_, index) => (
                                                                        <Circle key={`hole-${holeNumber}-player-circle-${index}`}
                                                                            sx={{ fontSize: 10 * factor, color: 'primary.main' }} />
                                                                      ))
                                                                    : Array.from({ length: Math.abs(holeScores.playerHcpAdj ?? 0) }, (_, index) => (
                                                                        <Add key={`hole-${holeNumber}-player-add-${index}`}
                                                                            sx={{ fontSize: 12 * factor, color: 'primary.main', fontWeight: 700 }} />
                                                                      )))
                                                            }
                                                        </Stack>
                                                        <Typography sx={{
                                                            ...holeValueStyle(displayScore || 0, teeSet.holes[holeNumber-1])
                                                        }}>
                                                            {displayScore || '-'}
                                                        </Typography>
                                                    </Stack>
                                                    
                                                    {isFrontNineEnd && (
                                                        <Stack 
                                                            key={`front-nine-${registration.scoreCode}`}
                                                            sx={{ 
                                                                width: 50, 
                                                                height: 60, 
                                                                minWidth: 50,
                                                                borderBottom: '1px solid darkgray',
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                                backgroundColor: 'sepia.extraLight',
                                                                fontWeight: 'bold'
                                                            }}
                                                        >
                                                            <Typography fontWeight="550" variant="subtitle2">
                                                                {playerTotal.front || '-'}
                                                            </Typography>
                                                        </Stack>
                                                    )}
                                                    
                                                    {isBackNineEnd && (
                                                        <Stack 
                                                            key={`back-nine-${registration.scoreCode}`}
                                                            sx={{ 
                                                                width: 50, 
                                                                height: 60, 
                                                                minWidth: 50,
                                                                borderBottom: '1px solid darkgray',
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                                backgroundColor: 'sepia.extraLight',
                                                                fontWeight: '550'
                                                            }}
                                                        >
                                                            <Typography fontWeight="550" variant="subtitle2">
                                                                {playerTotal.back || '-'}
                                                            </Typography>
                                                        </Stack>
                                                    )}
                                                    
                                                    {i === totalHoles-1 && (
                                                        <React.Fragment key={`totals-${registration.scoreCode}`}>
                                                            <Stack 
                                                                key={`total-${registration.scoreCode}`}
                                                                sx={{ 
                                                                    width: 50, 
                                                                    height: 60, 
                                                                    minWidth: 50,
                                                                    borderBottom: '1px solid darkgray',
                                                                    justifyContent: 'center',
                                                                    alignItems: 'center',
                                                                    backgroundColor: 'primary.extraLight',
                                                                    fontWeight: '550'
                                                                }}
                                                            >
                                                                <Typography fontWeight="550" variant="subtitle2">
                                                                    {playerTotal.total || '-'}
                                                                </Typography>
                                                            </Stack>
                                                            
                                                            <Stack 
                                                                key={`par-${registration.scoreCode}`}
                                                                sx={{ 
                                                                    width: 50, 
                                                                    height: 60, 
                                                                    minWidth: 50,
                                                                    borderBottom: '1px solid darkgray',
                                                                    justifyContent: 'center',
                                                                    alignItems: 'center',
                                                                    backgroundColor: 'sepia.background',
                                                                    fontWeight: '550'
                                                                }}
                                                            >
                                                                <Typography 
                                                                    fontWeight="550"
                                                                    variant="subtitle2"
                                                                    sx={{
                                                                        color: () => {
                                                                            if (playerTotal.total === 0 || playerTotal.totalPar === 0) return 'inherit';
                                                                            if (playerTotal.total < playerTotal.totalPar) return 'green';
                                                                            if (playerTotal.total > playerTotal.totalPar) return 'red';
                                                                            return 'inherit'; // Par
                                                                        }
                                                                    }}
                                                                >
                                                                    {(() => {
                                                                        if (playerTotal.total === 0 || playerTotal.totalPar === 0) return '-';
                                                                        
                                                                        const relativeToPar = playerTotal.total - playerTotal.totalPar;
                                                                        if (relativeToPar === 0) return 'E';
                                                                        if (relativeToPar > 0) return `+${relativeToPar}`;
                                                                        return relativeToPar.toString();
                                                                    })()}
                                                                </Typography>
                                                            </Stack>
                                                        </React.Fragment>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </Stack>
                                );
                            })}
                        </Stack>
                    </Stack>
                </Stack>
        </Stack>
    );
};
